/**
 * Reserved company-slug blocklist for the bare-root tenant scheme (`/:slug/…`).
 *
 * When a company slug can sit at the URL root, it shares one namespace with
 * every top-level application route. A company whose slug is `settings`,
 * `login`, or `reports` would shadow (or be shadowed by) the real page. This
 * module is the single source of truth for the words a slug may NOT be.
 *
 * The list is DERIVED, not hand-copied: the route-collision half is computed
 * from `routeConfig`'s `routes`, so adding a new top-level route automatically
 * reserves its first segment — the list can't drift out of sync the way a
 * duplicated literal array would. The static half covers infra/reserved words
 * that aren't app routes today but must never become a slug (`api`, `www`,
 * `admin`, the retired `c` namespace, deploy-preview prefixes, etc.).
 *
 * Kept co-located with `routeConfig` (not in the deliberately import-free
 * `common/hooks/companyPath.ts`) because it imports the route table.
 */

import { routes } from "./routeConfig";

/**
 * Keep `c` unavailable as a company slug. It was briefly used as a routing
 * namespace and remains too ambiguous to reuse as tenant identity.
 */
const RESERVED_COMPANY_SLUG = "c";

/**
 * First path segment of a routeConfig path, or `""` for the root (`/`).
 * `"/settings/hours"` → `"settings"`, `"/agents/:id"` → `"agents"`.
 */
const firstSegment = (path: string): string => path.split("/")[1] ?? "";

/**
 * Every top-level route segment currently in the app. Includes `:param`-free
 * literals only. The bare `/:slug` tenant mount is declared in JSX, not in
 * `routeConfig`, so no dynamic segment leaks in here.
 */
const routeReservedSlugs: readonly string[] = routes
  .map((route) => firstSegment(route.path))
  .filter((segment) => segment.length > 0 && !segment.startsWith(":"));

/**
 * Infrastructure / conventional reserved words that are NOT app routes today
 * but must never be claimable as a slug — either they're served outside the SPA
 * (`api`, `assets`, `static`), they're deploy/base-path prefixes (`pr-*` preview
 * builds — matched by prefix in `isReservedSlug`, listed here as the bare token
 * for documentation), they're JSX-only top-level redirect routes not represented
 * in routeConfig, or they're words a future route is likely to want.
 *
 * `c` is retained as an unavailable slug after its short-lived routing use.
 */
const STATIC_RESERVED_SLUGS: readonly string[] = [
  RESERVED_COMPANY_SLUG,
  // Served outside the SPA / reverse-proxy paths.
  "api",
  "www",
  "cdn",
  "static",
  "assets",
  "public",
  "media",
  "files",
  "images",
  "img",
  "fonts",
  // Auth + session surfaces.
  "auth",
  "oauth",
  "sso",
  "login",
  "service-portal",
  "logout",
  "signin",
  "signup",
  "register",
  "callback",
  // Ops / meta.
  "admin",
  "app",
  "status",
  "health",
  "healthz",
  "metrics",
  "docs",
  "support",
  "billing",
  "reporting",
  "stats",
  "webhooks",
  "graphql",
  // Retired staff routes. Keep their namespace unavailable to company slugs
  // without keeping the old pages or redirect aliases alive.
  "accounts",
  "company",
  "insights",
  "industry-insights",
  "marketing-website",
  "website",
  "manage-settings",
  "theme-studio",
  "partners",
  "report-email-preview",
  "disposition-backfill",
  "staff",
  "robots.txt",
  "favicon.ico",
  "sitemap.xml",
  ".well-known",
  // Values that break routing or read as placeholders.
  "null",
  "undefined",
  "none",
  "new",
  "edit",
  "index",
  "root",
];

/** Deploy-preview base-path prefix (`app-stg.sameday.ai/pr-<N>/`). A slug that
 * begins with this would collide with a subpath deploy. */
const PREVIEW_DEPLOY_SLUG_PREFIX = "pr-";

/**
 * Every reserved slug (route segments ∪ static infra words), normalized to
 * lowercase. A slug matching any of these — or beginning with the preview-deploy
 * prefix (see `isReservedSlug`) — must be rejected at company create/edit time.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set(
  [...routeReservedSlugs, ...STATIC_RESERVED_SLUGS].map((slug) =>
    slug.toLowerCase(),
  ),
);

/**
 * Whether a candidate slug collides with a reserved word or prefix and must be
 * rejected. Input is normalized (trimmed + lowercased) so callers can pass the
 * raw field value; slug generation lowercases anyway, but validating the raw
 * input fails closed if a caller forgets.
 *
 * @example
 * isReservedSlug("settings")   // true  (real route)
 * isReservedSlug("Reports")    // true  (case-insensitive)
 * isReservedSlug("api")        // true  (infra)
 * isReservedSlug("pr-1234")    // true  (deploy-preview prefix)
 * isReservedSlug("acme-hvac")  // false
 */
export const isReservedSlug = (slug: string): boolean => {
  const normalized = slug.trim().toLowerCase();
  if (normalized.startsWith(PREVIEW_DEPLOY_SLUG_PREFIX)) {
    return true;
  }
  return RESERVED_SLUGS.has(normalized);
};
